import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  UserCheck,
  Ticket,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Función para formatear fechas
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-');
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

export default function VerificarTicketPage() {
  const { ticketId } = useParams();
  const { isAuthenticated, trabajador, user } = useAuth();
  const isAdmin = user?.rol === 'admin' || user?.rol === 'administracion';
  
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinResult, setCheckinResult] = useState(null);

  useEffect(() => {
    verificarTicket();
  }, [ticketId]);

  const verificarTicket = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/eventos/verificar-ticket/${ticketId}`);
      setVerificationResult(res.data);
    } catch (err) {
      setVerificationResult({ valid: false, error: 'Error al verificar ticket' });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async () => {
    setCheckingIn(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/eventos/checkin/${ticketId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCheckinResult(res.data);
      // Actualizar el estado de verificación
      setVerificationResult(prev => ({
        ...prev,
        checked_in: true,
        checked_in_at: res.data.checked_in_at
      }));
    } catch (err) {
      setCheckinResult({ error: err.response?.data?.detail || 'Error al hacer check-in' });
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const { valid, error, evento, checked_in, checked_in_at, respuestas } = verificationResult;

  // Obtener nombre del asistente de las respuestas
  let nombreAsistente = '';
  if (respuestas) {
    for (const [key, value] of Object.entries(respuestas)) {
      if (key.toLowerCase().includes('nombre') && typeof value === 'string') {
        nombreAsistente = value;
        break;
      }
    }
    for (const [key, value] of Object.entries(respuestas)) {
      if (key.toLowerCase().includes('apellido') && typeof value === 'string') {
        nombreAsistente += ` ${value}`;
        break;
      }
    }
  }
  nombreAsistente = nombreAsistente.trim() || 'Asistente';

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-md mx-auto pt-8">
        {/* Header */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <img src="/logo-colegio.png" alt="CATSLZ" className="h-12" />
          </Link>
          <h1 className="text-xl font-bold text-gray-900 flex items-center justify-center gap-2">
            <Ticket className="w-5 h-5 text-violet-600" />
            Verificación de Entrada
          </h1>
        </div>

        <Card className="overflow-hidden shadow-lg">
          <CardContent className="p-0">
            {/* Estado principal */}
            <div className={`p-8 text-center ${
              !valid ? 'bg-red-50' : 
              checked_in ? 'bg-blue-50' : 
              'bg-green-50'
            }`}>
              {!valid ? (
                <>
                  <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-red-700 mb-2">Entrada Inválida</h2>
                  <p className="text-red-600">{error}</p>
                </>
              ) : checked_in ? (
                <>
                  <AlertCircle className="w-20 h-20 text-blue-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-blue-700 mb-2">Ya Ingresado</h2>
                  <p className="text-blue-600">Este ticket ya fue utilizado</p>
                  {checked_in_at && (
                    <p className="text-blue-500 text-sm mt-2">
                      Ingreso: {new Date(checked_in_at).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-green-700 mb-2">Entrada Válida</h2>
                  <p className="text-green-600">Puede ingresar al evento</p>
                </>
              )}
            </div>

            {/* Código del ticket */}
            <div className="px-6 py-4 bg-gray-50 border-y text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Código</p>
              <p className="text-2xl font-mono font-bold text-gray-900 tracking-widest">
                {ticketId.toUpperCase()}
              </p>
            </div>

            {/* Info del asistente y evento */}
            {valid && evento && (
              <div className="p-6">
                {/* Asistente */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-violet-50 rounded-lg">
                  <UserCheck className="w-8 h-8 text-violet-600" />
                  <div>
                    <p className="text-xs text-violet-600 uppercase">Asistente</p>
                    <p className="font-semibold text-gray-900">{nombreAsistente}</p>
                  </div>
                </div>

                {/* Evento */}
                <h3 className="font-semibold text-gray-900 mb-3">{evento.nombre}</h3>
                
                <div className="space-y-2 text-sm text-gray-600">
                  {evento.fecha_evento && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-violet-500" />
                      <span>{formatDate(evento.fecha_evento)}</span>
                    </div>
                  )}
                  {evento.hora_evento && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-violet-500" />
                      <span>{evento.hora_evento} hs</span>
                    </div>
                  )}
                  {evento.lugar && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-violet-500" />
                      <span>{evento.lugar}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Botón de check-in (solo admin) */}
            {valid && !checked_in && isAdmin && (
              <div className="p-4 border-t bg-gray-50">
                {checkinResult?.error ? (
                  <div className="text-center text-red-600 mb-3">
                    {checkinResult.error}
                  </div>
                ) : null}
                
                <Button 
                  onClick={handleCheckin}
                  disabled={checkingIn}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="btn-checkin"
                >
                  {checkingIn ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Registrando...
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Registrar Ingreso
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Mensaje si ya se hizo check-in */}
            {checkinResult && !checkinResult.error && (
              <div className="p-4 bg-green-100 border-t">
                <p className="text-center text-green-700 font-medium flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  {checkinResult.already_checked ? 'Ya estaba registrado' : 'Ingreso registrado exitosamente'}
                </p>
              </div>
            )}

            {/* Mensaje para usuarios no admin */}
            {valid && !checked_in && !isAdmin && (
              <div className="p-4 border-t bg-amber-50">
                <p className="text-center text-amber-700 text-sm">
                  Mostrá esta pantalla al personal de ingreso
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Botón de volver a verificar */}
        <div className="mt-6 text-center">
          <Button 
            variant="outline" 
            onClick={verificarTicket}
            className="text-gray-600"
            data-testid="btn-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar estado
          </Button>
        </div>
      </div>
    </div>
  );
}
